// Iter 226 — Period Picker (Popover) shared antara PerformancePage (Owner) & AdminTokoDashboard.
//   Tokens periode:
//     - today, week, month, year         → quick chips (dari label real: "Hari Ini" dst.)
//     - date (from_=YYYY-MM-DD)          → tanggal tertentu
//     - specific_month (from_=YYYY-MM-01)→ bulan tertentu
//     - specific_year (from_=YYYY-01-01) → tahun tertentu
//     - range (from_=..., to=...)        → rentang custom (Owner-only)
//   Props:
//     - value: { period, from, to }
//     - onChange({period,from,to})
//     - allowRange (boolean) — Owner true, Admin Toko false
//   Layout: chip row + tombol "Pilih Periode" (Calendar icon) yang membuka popover di bawah.
import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";

const todayISOWIB = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());
const MONTHS_ID = ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"];
const MONTHS_ID_SHORT = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];

export function formatPeriodLabel(period, from, to) {
  const t = todayISOWIB();
  if (period === "today") return "Hari Ini";
  if (period === "week")  return "Minggu Ini";
  if (period === "month") return "Bulan Ini";
  if (period === "year")  return "Tahun Ini";
  if (period === "date" && from) {
    const [y,m,d] = from.split("-");
    return `${parseInt(d,10)} ${MONTHS_ID_SHORT[parseInt(m,10)-1]} ${y}`;
  }
  if (period === "specific_month" && from) {
    const [y,m] = from.split("-");
    return `${MONTHS_ID[parseInt(m,10)-1]} ${y}`;
  }
  if (period === "specific_year" && from) return `Tahun ${from.slice(0,4)}`;
  if (period === "range" && from && to) {
    const fmt = (iso) => { const [y,m,d]=iso.split("-"); return `${parseInt(d,10)} ${MONTHS_ID_SHORT[parseInt(m,10)-1]} ${y}`; };
    return `${fmt(from)} – ${fmt(to)}`;
  }
  return "—";
}

export function formatPrevLabel(prevFrom, prevTo) {
  if (!prevFrom || !prevTo) return "";
  if (prevFrom === prevTo) {
    const [y,m,d]=prevFrom.split("-"); return `${parseInt(d,10)} ${MONTHS_ID_SHORT[parseInt(m,10)-1]} ${y}`;
  }
  // Jika bulan penuh
  const [y1,m1,d1]=prevFrom.split("-"); const [y2,m2,d2]=prevTo.split("-");
  if (d1 === "01" && (parseInt(d2,10) >= 28) && m1===m2 && y1===y2) return `${MONTHS_ID_SHORT[parseInt(m1,10)-1]} ${y1}`;
  if (y1===y2 && m1==="01" && m2==="12" && d1==="01" && d2==="31") return `${y1}`;
  return `${parseInt(d1,10)} ${MONTHS_ID_SHORT[parseInt(m1,10)-1]} – ${parseInt(d2,10)} ${MONTHS_ID_SHORT[parseInt(m2,10)-1]} ${y2}`;
}

const QUICK = [
  { id: "today", label: "Hari Ini" },
  { id: "week",  label: "Minggu Ini" },
  { id: "month", label: "Bulan Ini" },
  { id: "year",  label: "Tahun Ini" },
];

export function PeriodPicker({ value, onChange, allowRange = false, testidPrefix = "period" }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(() => {
    if (value?.period === "date") return "date";
    if (value?.period === "specific_month") return "month";
    if (value?.period === "specific_year") return "year";
    if (value?.period === "range") return "range";
    return "date";
  });
  const t = todayISOWIB();
  const [dateVal,  setDateVal]  = useState(value?.period==="date" ? value.from : t);
  const [monthVal, setMonthVal] = useState(value?.period==="specific_month" ? value.from.slice(0,7) : t.slice(0,7));
  const [yearVal,  setYearVal]  = useState(value?.period==="specific_year" ? value.from.slice(0,4) : t.slice(0,4));
  const [rangeFrom, setRangeFrom] = useState(value?.period==="range" ? value.from : t.slice(0,8)+"01");
  const [rangeTo,   setRangeTo]   = useState(value?.period==="range" ? value.to   : t);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  // Sync internal state when value changes externally (e.g., quick chip clicked)
  useEffect(() => {
    if (value?.period === "date") { setMode("date"); setDateVal(value.from || t); }
    else if (value?.period === "specific_month") { setMode("month"); setMonthVal((value.from || t).slice(0,7)); }
    else if (value?.period === "specific_year") { setMode("year"); setYearVal((value.from || t).slice(0,4)); }
    else if (value?.period === "range") { setMode("range"); setRangeFrom(value.from || t); setRangeTo(value.to || t); }
  }, [value?.period, value?.from, value?.to]);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const activePreset = ["today","week","month","year"].includes(value?.period) ? value.period : null;

  const apply = () => {
    if (mode === "date") {
      onChange({ period: "date", from: dateVal, to: dateVal });
    } else if (mode === "month") {
      onChange({ period: "specific_month", from: monthVal + "-01", to: "" }); // backend derives last day
    } else if (mode === "year") {
      onChange({ period: "specific_year", from: yearVal + "-01-01", to: "" });
    } else if (mode === "range") {
      if (!rangeFrom || !rangeTo || rangeFrom > rangeTo) return;
      onChange({ period: "range", from: rangeFrom, to: rangeTo });
    }
    setOpen(false);
  };

  // Year list: last 10 years + current
  const curYear = parseInt(t.slice(0,4), 10);
  const years = useMemo(() => {
    const out = [];
    for (let y = curYear; y >= curYear - 10; y--) out.push(String(y));
    return out;
  }, [curYear]);

  return <div className="period-picker" data-testid={testidPrefix}>
    <div className="period-picker-chips" data-testid={`${testidPrefix}-chips`}>
      {QUICK.map(q => <button
        key={q.id}
        type="button"
        className={`period-chip ${activePreset===q.id ? "active" : ""}`}
        data-testid={`${testidPrefix}-${q.id}`}
        onClick={()=>{
          if (q.id === "today") onChange({ period: "today", from: t, to: t });
          else if (q.id === "week")  { const d=new Date(t+"T00:00:00"); const monday=new Date(d); monday.setDate(d.getDate()-((d.getDay()+6)%7)); const fromISO=monday.toISOString().slice(0,10); onChange({ period: "week", from: fromISO, to: t }); }
          else if (q.id === "month") onChange({ period: "month", from: t.slice(0,8)+"01", to: t });
          else if (q.id === "year")  onChange({ period: "year", from: t.slice(0,4)+"-01-01", to: t });
        }}
      >{q.label}</button>)}
      <button
        ref={btnRef}
        type="button"
        className={`period-chip period-pick-btn ${!activePreset ? "active" : ""}`}
        data-testid={`${testidPrefix}-open`}
        onClick={()=>setOpen(o=>!o)}
        aria-expanded={open}
      ><CalendarIcon size={14}/> Pilih Periode <ChevronDown size={14}/></button>
    </div>
    {open && <div ref={popRef} className="period-picker-pop" data-testid={`${testidPrefix}-pop`} role="dialog">
      <div className="period-pop-tabs" role="tablist">
        <button type="button" className={mode==="date"?"active":""} data-testid={`${testidPrefix}-mode-date`} onClick={()=>setMode("date")}>Tanggal</button>
        <button type="button" className={mode==="month"?"active":""} data-testid={`${testidPrefix}-mode-month`} onClick={()=>setMode("month")}>Bulan</button>
        <button type="button" className={mode==="year"?"active":""} data-testid={`${testidPrefix}-mode-year`} onClick={()=>setMode("year")}>Tahun</button>
        {allowRange && <button type="button" className={mode==="range"?"active":""} data-testid={`${testidPrefix}-mode-range`} onClick={()=>setMode("range")}>Rentang</button>}
      </div>
      <div className="period-pop-body">
        {mode === "date" && <label className="period-field">
          <span>Pilih tanggal</span>
          <input type="date" value={dateVal} max={t} onChange={e=>setDateVal(e.target.value)} data-testid={`${testidPrefix}-input-date`}/>
        </label>}
        {mode === "month" && <label className="period-field">
          <span>Pilih bulan</span>
          <input type="month" value={monthVal} max={t.slice(0,7)} onChange={e=>setMonthVal(e.target.value)} data-testid={`${testidPrefix}-input-month`}/>
        </label>}
        {mode === "year" && <label className="period-field">
          <span>Pilih tahun</span>
          <select value={yearVal} onChange={e=>setYearVal(e.target.value)} data-testid={`${testidPrefix}-input-year`}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>}
        {mode === "range" && allowRange && <div className="period-range-inputs">
          <label className="period-field"><span>Dari</span>
            <input type="date" value={rangeFrom} max={t} onChange={e=>setRangeFrom(e.target.value)} data-testid={`${testidPrefix}-input-range-from`}/>
          </label>
          <label className="period-field"><span>Sampai</span>
            <input type="date" value={rangeTo} max={t} onChange={e=>setRangeTo(e.target.value)} data-testid={`${testidPrefix}-input-range-to`}/>
          </label>
        </div>}
      </div>
      <div className="period-pop-actions">
        <button type="button" className="period-btn-secondary" data-testid={`${testidPrefix}-cancel`} onClick={()=>setOpen(false)}>Batal</button>
        <button type="button" className="period-btn-primary" data-testid={`${testidPrefix}-apply`} onClick={apply}>Terapkan</button>
      </div>
    </div>}
  </div>;
}
